import React, { useState } from "react";
import styled from "styled-components";
import Text from "../atoms/Text";
import Button from "../atoms/Button";
import Input from "../atoms/Input";
import DrugSearchInput from "../molecules/DrugSearchInput";
import { Prescription, Drug, EMPTY_PRESCRIPTION } from "../../common/models/types";
import { COLOR_SYSTEM } from "../design/design-system";

type InputState = "searching" | "linked" | "manual";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0px;
  background-color: #fff;
  border-radius: 8px;
  max-width: 700px;
  width: 700px;
  margin: auto;
`;

const Section = styled.div``;

const SectionHeader = styled.div`
  margin-bottom: 24px;
`;

const SectionDescription = styled.div`
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-size: 14px;
  margin-top: 8px;
  line-height: 1.6;
`;

const DescriptionBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  background: ${COLOR_SYSTEM.get("Skyblue")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Skyblue")?.[200]};
  border-radius: 8px;
`;

const DescriptionTitle = styled.div`
  color: ${COLOR_SYSTEM.get("Skyblue")?.[700]};
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const DescriptionList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  list-style: disc;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-size: 13px;

  li {
    margin-bottom: 4px;
  }
`;

const ItemCard = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  padding: 16px;
`;

const InputSection = styled.div<{ $state: InputState }>`
  background: ${({ $state }) =>
    $state === "linked" ? COLOR_SYSTEM.get("Skyblue")?.[10] : "#ffffff"};
  border: 1px solid
    ${({ $state }) =>
      $state === "linked" ? COLOR_SYSTEM.get("Skyblue")?.[200] : COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  min-height: 80px;
`;

const SelectedCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LinkedBadge = styled.span`
  display: inline-block;
  background: ${COLOR_SYSTEM.get("Skyblue")?.[100]};
  color: ${COLOR_SYSTEM.get("Skyblue")?.[700]};
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  vertical-align: middle;
`;

const ManualBadge = styled.span`
  display: inline-block;
  background: ${COLOR_SYSTEM.get("Gray")?.[100]};
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  margin-left: 8px;
  vertical-align: middle;
`;

const SubInfo = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
`;

const LinkedInfo = styled.p`
  color: ${COLOR_SYSTEM.get("Skyblue")?.[700]};
  font-size: 11px;
  margin-top: 10px;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

interface OTCFormTemplateProps {
  title?: string;
  initialPrescription?: Prescription;
  patientId?: number;
  onSubmit: (prescription: Prescription) => Promise<void>;
  onCancel: () => void;
}

const OTCFormTemplate: React.FC<OTCFormTemplateProps> = ({
  title,
  initialPrescription,
  patientId,
  onSubmit,
  onCancel,
}) => {
  const [prescription, setPrescription] = useState<Prescription>(() => {
    const basePrescription = initialPrescription
      ? { ...initialPrescription }
      : { ...EMPTY_PRESCRIPTION, type: "otc" };
    if (patientId && !basePrescription.patientId) {
      basePrescription.patientId = patientId;
    }
    return basePrescription;
  });

  const [inputState, setInputState] = useState<InputState>(() =>
    initialPrescription?.standardCode ? "linked" : "searching"
  );

  const [searchValue, setSearchValue] = useState<string>(() => {
    const initial = initialPrescription;
    return initial?.standardCode || initial?.medicationName || "";
  });

  const handleInputChange = (field: keyof Prescription, value: string | number) => {
    setPrescription((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDrugSelect = (drug: Drug) => {
    setPrescription((prev) => ({
      ...prev,
      standardCode: drug.standardCode || "",
      medicationName: drug.productName || "",
      itemCode: drug.itemCode || "",
      source: "manual",
      drug: drug,
    }));
    setSearchValue("");
    setInputState("linked");
  };

  const handleClear = () => {
    setPrescription((prev) => ({
      ...prev,
      standardCode: "",
      medicationName: "",
      itemCode: "",
      drug: undefined,
    }));
    setSearchValue("");
    setInputState("searching");
  };

  const handleManualInput = () => {
    setPrescription((prev) => ({
      ...prev,
      standardCode: "",
      medicationName: searchValue || prev.medicationName,
      itemCode: "",
      drug: undefined,
    }));
    setInputState("manual");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { drug, ...prescriptionToSave } = prescription;
    await onSubmit(prescriptionToSave);
  };

  const memoPlaceholder =
    inputState === "manual"
      ? "허가사항이 연동되지 않은 의약품입니다. AI가 정확히 분석할 수 있도록 성분이나 용법 등을 가급적 상세히 적어주세요."
      : "환자의 복용 목적이나 특이사항을 적어주세요.";

  return (
    <FormContainer>
      <Section>
        <SectionHeader>
          <Text textType="H3">{title}</Text>
          <SectionDescription>
            환자가 복용 중인 일반의약품을 등록하세요.
            <DescriptionBox>
              <DescriptionTitle>일반의약품 입력 안내</DescriptionTitle>
              <DescriptionList>
                <li>제품명으로 검색하고 목록에서 의약품을 선택해 주세요</li>
                <li>허가사항이 자동으로 연결되어 AI가 용법·용량, 주의사항 등을 함께 분석합니다</li>
                <li>등록된 제품이 아닌 경우 목록에서 '직접 입력하기'를 선택해 주세요</li>
                <li>직접 입력 시 메모에 주의사항이나 정보를 남겨주세요</li>
              </DescriptionList>
            </DescriptionBox>
          </SectionDescription>
        </SectionHeader>

        <ItemCard>
          <InputSection $state={inputState}>
            {inputState !== "linked" && (
              <div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                    의약품 검색
                  </Text>
                  {inputState === "manual" && <ManualBadge>정보 미연동</ManualBadge>}
                </div>
                <DrugSearchInput
                  value={searchValue}
                  onChange={(value) => {
                    setSearchValue(value);
                    if (inputState === "manual") {
                      handleInputChange("medicationName", value);
                    }
                  }}
                  onSelect={handleDrugSelect}
                  onManualInput={handleManualInput}
                  type="otc"
                  placeholder="제품명 입력 후 엔터를 눌러주세요"
                />
              </div>
            )}

            {inputState === "linked" && (
              <div>
                <SelectedCardHeader>
                  <div>
                    <Text
                      textType="Body1"
                      color={COLOR_SYSTEM.get("Skyblue")?.[800]}
                      style={{ fontWeight: 600 }}
                    >
                      {prescription.medicationName}
                    </Text>
                    {prescription.drug?.companyName && (
                      <SubInfo style={{ display: "inline", marginTop: 0, marginLeft: "8px" }}>
                        {prescription.drug.companyName}
                      </SubInfo>
                    )}
                  </div>
                  <Button
                    size="Small"
                    type="Text"
                    variant="Ghost"
                    color="Secondary"
                    onClick={handleClear}
                  >
                    지우기
                  </Button>
                </SelectedCardHeader>
                <LinkedInfo>
                  <LinkedBadge>정보 연동됨</LinkedBadge> 허가사항이 연동되었습니다. AI 검토에
                  반영됩니다.
                </LinkedInfo>
              </div>
            )}
          </InputSection>

          <div>
            <Text
              textType="Body2"
              color={COLOR_SYSTEM.get("Gray")?.[700]}
              style={{ marginBottom: "4px" }}
            >
              메모{" "}
              <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[400]}>
                ({inputState === "manual" ? "권장" : "선택사항"})
              </Text>
            </Text>
            <Input
              multiLine
              placeholder={memoPlaceholder}
              value={prescription.notes || ""}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              style={{ minHeight: "80px" }}
            />
          </div>
        </ItemCard>

        <ButtonContainer>
          <Button
            buttonType="button"
            color="Tertiary"
            size="Medium"
            type="Text"
            variant="Outlined"
            onClick={onCancel}
          >
            취소
          </Button>
          <Button
            buttonType="submit"
            color="Primary"
            size="Medium"
            type="IconWithText"
            variant="Contained"
            icon="Check"
            left
            onClick={handleSubmit}
            disabled={!prescription.medicationName}
            disabledColor={!prescription.medicationName}
          >
            저장
          </Button>
        </ButtonContainer>
      </Section>
    </FormContainer>
  );
};

export default OTCFormTemplate;
